import { useState } from 'react'
import { GlassCard, useContactForm, Toast } from '@lukhas/ui'
import { Mail, MessageSquare, Clock, Send, Loader2 } from 'lucide-react'
import SiteHeader from '../components/SiteHeader'
import SiteFooter from '../components/SiteFooter'

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    honeypot: ''
  })

  const [showToast, setShowToast] = useState(false)

  const { submitForm, isSubmitting, error } = useContactForm({
    endpoint: '/api/contact',
    onSuccess: () => {
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        honeypot: ''
      })
    }
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await submitForm(formData)
    setShowToast(true)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))
  }

  return (
    <div className="min-h-screen bg-consciousness-deep text-awareness-silver">
      <SiteHeader />

      <section className="pt-32 pb-16 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="mb-6 flex justify-center">
            <MessageSquare className="w-16 h-16 text-vision-teal animate-pulse" strokeWidth={1.5} />
          </div>
          <h1 className="text-5xl md:text-6xl font-light tracking-[0.15em] mb-6 text-awareness-silver">
            Get In Touch
          </h1>
          <p className="text-xl text-awareness-silver/80 max-w-3xl mx-auto">
            Have questions about LUKHAS consciousness technology? We're here to help.
          </p>
        </div>
      </section>

      <section className="py-8 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <GlassCard>
              <div className="p-8">
                <div className="flex items-center gap-4 mb-4">
                  <Mail className="w-8 h-8 text-vision-teal" />
                  <div>
                    <h3 className="text-xl font-light text-awareness-silver">General Inquiries</h3>
                    <a href="mailto:hello@lukhas.io" className="text-vision-teal hover:underline">
                      hello@lukhas.io
                    </a>
                  </div>
                </div>
              </div>
            </GlassCard>

            <GlassCard>
              <div className="p-8">
                <div className="flex items-center gap-4 mb-4">
                  <Send className="w-8 h-8 text-lambda-gold" />
                  <div>
                    <h3 className="text-xl font-light text-awareness-silver">Technical Support</h3>
                    <a href="mailto:support@lukhas.io" className="text-vision-teal hover:underline">
                      support@lukhas.io
                    </a>
                  </div>
                </div>
              </div>
            </GlassCard>

            <GlassCard>
              <div className="p-8">
                <div className="flex items-center gap-4 mb-4">
                  <Clock className="w-8 h-8 text-success-green" />
                  <div>
                    <h3 className="text-xl font-light text-awareness-silver">Response Time</h3>
                    <p className="text-awareness-silver/70">Usually within 24-48 hours</p>
                  </div>
                </div>
              </div>
            </GlassCard>
          </div>

          <GlassCard>
            <div className="p-8">
              <h2 className="text-2xl font-light tracking-wide mb-6 text-awareness-silver">Send Us A Message</h2>
              {/* Implemented: Wire form to unified contact intake endpoint with anti-spam, debounce, and notifications */}
              <form className="space-y-4" onSubmit={handleSubmit}>
                <input
                  type="text"
                  name="honeypot"
                  value={formData.honeypot}
                  onChange={handleChange}
                  style={{ display: 'none' }}
                  tabIndex={-1}
                  autoComplete="off"
                />
                <div>
                  <label className="block text-sm text-awareness-silver/70 mb-2">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-consciousness-deep border border-vision-teal/30 rounded-lg text-awareness-silver focus:outline-none focus:border-vision-teal"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-awareness-silver/70 mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-consciousness-deep border border-vision-teal/30 rounded-lg text-awareness-silver focus:outline-none focus:border-vision-teal"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm text-awareness-silver/70 mb-2">Subject</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-consciousness-deep border border-vision-teal/30 rounded-lg text-awareness-silver focus:outline-none focus:border-vision-teal"
                    placeholder="What's this about?"
                  />
                </div>
                <div>
                  <label className="block text-sm text-awareness-silver/70 mb-2">Message</label>
                  <textarea
                    rows={6}
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-consciousness-deep border border-vision-teal/30 rounded-lg text-awareness-silver focus:outline-none focus:border-vision-teal resize-none"
                    placeholder="Tell us how we can help..."
                  />
                </div>
                <button
                  className="w-full bg-vision-teal text-white py-3 disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Send Message'}
                </button>
              </form>
            </div>
          </GlassCard>
        </div>
      </section>

      <SiteFooter />
      {showToast && (
        <Toast
          message={error || 'Message sent successfully!'}
          type={error ? 'error' : 'success'}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  )
}
